/**
 * What Activity says about a category change.
 *
 * Shardul, QA, 22 Sep 26: "when i add one category and remove one, it doesnt
 * show up in realtime, the activity trail for it also breaks and is showing
 * wrong thing". The wrong thing was literal. Both halves of the categories
 * bridge carry the same field label, "Additional categories", so the live
 * check announced a removal with "Google now shows the additional categories
 * the agent sent (Juice bar)": the exact opposite of what happened. The add
 * fared no better, saying only "Applied to your Google profile" without ever
 * naming the category.
 *
 * So the wording is worked out here, by bridge, and always names the
 * categories. Pure, so the guard test reads it directly
 * (tests/agent/unit/category-lines.test.ts).
 */

/** "Juice bar", "Juice bar and Bakery", "Juice bar, Bakery and Cafe". */
export function nameList(names: string[]): string {
  const clean = names.map((n) => String(n ?? '').trim()).filter(Boolean);
  if (!clean.length) return '';
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

/** A category that came off the profile, in the owner's words. */
export function removalActivityLine(names: string[]): string {
  const list = nameList(names);
  return list
    ? `Removed ${list} from your categories on Google.`
    : 'Removed that category from your categories on Google.';
}

/** A category that went on the profile, in the owner's words. */
export function additionActivityLine(names: string[]): string {
  const list = nameList(names);
  return list
    ? `Added ${list} to your categories on Google.`
    : 'Added the categories you picked to your Google profile.';
}

/** The four moments a tracked change is told about. */
export type CategoryMoment = 'done' | 'live' | 'not_live' | 'reverted';

/**
 * One sentence for a category change, or null when the bridge is not one of
 * the two category writes (every other field keeps its own wording).
 *
 * done      the write went to Google and was accepted
 * live      the verify pass read the profile back and the change is showing
 * not_live  48 hours on and Google still shows the old list
 * reverted  Google had shown the change and has taken it back
 */
export function categoryActivityLine(bridge: string, names: string[], moment: CategoryMoment): string | null {
  const list = nameList(names);
  if (bridge === 'categories_remove_publish') {
    switch (moment) {
      case 'done': return removalActivityLine(names);
      case 'live': return list ? `Google no longer lists ${list} under your categories.` : 'Google no longer lists that category under your categories.';
      case 'not_live': return list ? `Google still lists ${list} under your categories.` : 'Google still lists that category under your categories.';
      case 'reverted': return list ? `${list} is back under your categories on Google.` : 'That category is back under your categories on Google.';
    }
  }
  if (bridge === 'categories_publish') {
    switch (moment) {
      case 'done': return additionActivityLine(names);
      case 'live': return list ? `Google now lists ${list} under your categories.` : 'Google now lists the categories you picked.';
      case 'not_live': return list ? `Google does not list ${list} under your categories yet.` : 'Google does not list the new categories yet.';
      case 'reverted': return list ? `${list} is off your categories on Google again.` : 'The new categories are off your Google profile again.';
    }
  }
  return null;
}

/**
 * The category names behind a verification record. The add stores them as a
 * comma list ("Juice bar, Bakery"), the removal as the one display name.
 */
export function namesFromExpected(expected: unknown): string[] {
  return String(expected ?? '')
    .split(',')
    .map((n) => n.trim())
    .filter(Boolean);
}
